import React, { useState } from 'react';
import { X, ArrowRight, Check, BookOpen, Award, Sparkles, Clock, Target } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
import { StoryPlayer } from './StoryPlayer';
import { QuizPlayer } from './QuizPlayer';
import { getStoriesForAge } from '../data/storiesData';
import { getQuizzesForAge } from '../data/quizzesData';
interface ModuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}
export function ModuleModal({
  isOpen,
  onClose,
  moduleId,
  title,
  description,
  icon,
  color
}: ModuleModalProps) {
  const {
    progress
  } = useProgress();
  const [selectedStory, setSelectedStory] = useState<string | null>(null);
  const [selectedQuiz, setSelectedQuiz] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'stories' | 'quizzes'>('stories');
  const userAge = progress.preferences.age || 16;
  const userGender = progress.preferences.gender || 'prefer-not-to-say';
  const userName = progress.preferences.name || 'Friend';
  const categoryForModule = getCategoryFromModule(moduleId);
  const availableStories = getStoriesForAge(userAge, userGender).filter(story => story.category === categoryForModule);
  const availableQuizzes = getQuizzesForAge(userAge).filter(quiz => quiz.category === categoryForModule);
  if (!isOpen) return null;
  if (selectedStory) {
    return <div className="fixed inset-0 z-50" data-id="element-114">
        <StoryPlayer storyId={selectedStory} onExit={() => setSelectedStory(null)} data-id="element-115" />
      </div>;
  }
  if (selectedQuiz) {
    return <div className="fixed inset-0 z-50" data-id="element-116">
        <QuizPlayer quizId={selectedQuiz} onExit={() => setSelectedQuiz(null)} data-id="element-117" />
      </div>;
  }
  const getCategoryColor = (category: string) => {
    const colors = {
      consent: 'from-indigo-500 to-blue-500',
      relationships: 'from-teal-500 to-cyan-500',
      safety: 'from-orange-500 to-amber-500',
      health: 'from-emerald-500 to-green-500',
      emotions: 'from-blue-500 to-indigo-500',
      online: 'from-green-500 to-emerald-500'
    };
    return colors[category as keyof typeof colors] || 'from-gray-500 to-slate-500';
  };
  const getDifficultyBadge = (difficulty: string) => {
    const badges = {
      easy: {
        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        label: 'Beginner'
      },
      medium: {
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        label: 'Intermediate'
      },
      hard: {
        color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        label: 'Advanced'
      }
    };
    return badges[difficulty as keyof typeof badges] || badges.medium;
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in" data-id="element-118">
      <div className="w-full max-w-7xl max-h-[90vh] bg-white dark:bg-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-scale-in" data-id="element-119">
        {/* Header */}
        <div className={`relative bg-gradient-to-r ${color} p-6 md:p-8 flex items-center justify-between flex-shrink-0 overflow-hidden`} data-id="element-120">
          <div className="absolute inset-0 pattern-dots opacity-10" data-id="element-121" />
          <div className="relative flex items-center space-x-4 flex-1" data-id="element-122">
            <div className="w-16 h-16 md:w-20 md:h-20 glass-effect rounded-2xl flex items-center justify-center shadow-lg" data-id="element-123">
              {icon}
            </div>
            <div className="flex-1" data-id="element-124">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-1" data-id="element-125">
                {title}
              </h2>
              <p className="text-white/90 text-sm md:text-lg flex items-center space-x-2" data-id="element-126">
                <Sparkles className="w-4 h-4" data-id="element-127" />
                <span data-id="element-128">Hey {userName}, let's explore together!</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="relative w-10 h-10 md:w-12 md:h-12 glass-effect hover:bg-white/30 rounded-full flex items-center justify-center transition-all flex-shrink-0 group" data-id="element-129">
            <X className="w-5 h-5 md:w-6 md:h-6 text-white group-hover:rotate-90 transition-transform" data-id="element-130" />
          </button>
        </div>
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-slate-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 flex-shrink-0" data-id="element-131">
          <button onClick={() => setActiveTab('stories')} className={`flex-1 py-4 px-6 font-semibold transition-all relative group ${activeTab === 'stories' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-132">
            <div className="flex items-center justify-center space-x-2" data-id="element-133">
              <BookOpen className="w-5 h-5" data-id="element-134" />
              <span data-id="element-135">Stories</span>
              <span className={`px-2 py-1 rounded-full text-xs font-bold ${activeTab === 'stories' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-400'}`} data-id="element-136">
                {availableStories.length}
              </span>
            </div>
            {activeTab === 'stories' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500 animate-slide-up" data-id="element-137" />}
          </button>
          <button onClick={() => setActiveTab('quizzes')} className={`flex-1 py-4 px-6 font-semibold transition-all relative group ${activeTab === 'quizzes' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-138">
            <div className="flex items-center justify-center space-x-2" data-id="element-139">
              <Award className="w-5 h-5" data-id="element-140" />
              <span data-id="element-141">Quizzes</span>
              <span className={`px-2 py-1 rounded-full text-xs font-bold ${activeTab === 'quizzes' ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-400'}`} data-id="element-142">
                {availableQuizzes.length}
              </span>
            </div>
            {activeTab === 'quizzes' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-teal-500 animate-slide-up" data-id="element-143" />}
          </button>
        </div>
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800" data-id="element-144">
          {activeTab === 'stories' && <div className="animate-fade-in" data-id="element-145">
              <div className="mb-8 text-center" data-id="element-146">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center justify-center space-x-2" data-id="element-147">
                  <BookOpen className="w-7 h-7 text-blue-500" data-id="element-148" />
                  <span data-id="element-149">Choose Your Adventure!</span>
                </h3>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-150">
                  Make choices and learn through real-life scenarios
                </p>
              </div>
              {availableStories.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-151">
                  {availableStories.map((story, index) => {
              const isCompleted = progress.completedStories.includes(story.id);
              return <button key={story.id} onClick={() => setSelectedStory(story.id)} className="group relative bg-white dark:bg-slate-700 rounded-2xl p-6 text-left transition-all hover:shadow-2xl hover:-translate-y-2 border-2 border-transparent hover:border-blue-300 dark:hover:border-blue-600 overflow-hidden animate-scale-in" style={{
                animationDelay: `${index * 100}ms`
              }} data-id="element-152">
                        <div className={`absolute inset-0 bg-gradient-to-br ${getCategoryColor(story.category)} opacity-0 group-hover:opacity-5 transition-opacity`} data-id="element-153" />
                        {isCompleted && <div className="absolute top-4 right-4 w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-bounce" data-id="element-154">
                            <Check className="w-5 h-5 text-white" data-id="element-155" />
                          </div>}
                        <div className="relative" data-id="element-156">
                          <div className={`w-14 h-14 bg-gradient-to-br ${getCategoryColor(story.category)} rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`} data-id="element-157">
                            <BookOpen className="w-7 h-7 text-white" data-id="element-158" />
                          </div>
                          <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:gradient-text group-hover:from-blue-600 group-hover:to-purple-600" data-id="element-159">
                            {story.title}
                          </h4>
                          <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2" data-id="element-160">
                            {story.description}
                          </p>
                          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400" data-id="element-161">
                            <div className="flex items-center space-x-1" data-id="element-162">
                              <Clock className="w-3 h-3" data-id="element-163" />
                              <span data-id="element-164">{story.estimatedTime} min</span>
                            </div>
                            <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 font-semibold group-hover:translate-x-1 transition-transform" data-id="element-165">
                              <span data-id="element-166">Start</span>
                              <ArrowRight className="w-4 h-4" data-id="element-167" />
                            </div>
                          </div>
                        </div>
                      </button>;
            })}
                </div> : <div className="text-center py-16 glass-effect glass-effect-dark rounded-2xl border border-gray-200 dark:border-slate-700" data-id="element-168">
                  <div className="text-6xl mb-4 animate-bounce" data-id="element-169">📚</div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg font-medium" data-id="element-170">
                    No stories available yet
                  </p>
                  <p className="text-gray-500 dark:text-gray-500 text-sm mt-2" data-id="element-171">
                    Check back soon for new adventures!
                  </p>
                </div>}
            </div>}
          {activeTab === 'quizzes' && <div className="animate-fade-in" data-id="element-172">
              <div className="mb-8 text-center" data-id="element-173">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center justify-center space-x-2" data-id="element-174">
                  <Target className="w-7 h-7 text-green-500" data-id="element-175" />
                  <span data-id="element-176">Test Your Knowledge!</span>
                </h3>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-177">
                  Challenge yourself and earn points
                </p>
              </div>
              {availableQuizzes.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-178">
                  {availableQuizzes.map((quiz, index) => {
              const isCompleted = progress.completedQuizzes.includes(quiz.id);
              const difficultyBadge = getDifficultyBadge(quiz.difficulty);
              return <button key={quiz.id} onClick={() => setSelectedQuiz(quiz.id)} className="group relative bg-white dark:bg-slate-700 rounded-2xl p-6 text-left transition-all hover:shadow-2xl hover:-translate-y-2 border-2 border-transparent hover:border-green-300 dark:hover:border-green-600 overflow-hidden animate-scale-in" style={{
                animationDelay: `${index * 100}ms`
              }} data-id="element-179">
                        <div className={`absolute inset-0 bg-gradient-to-br ${getCategoryColor(quiz.category)} opacity-0 group-hover:opacity-5 transition-opacity`} data-id="element-180" />
                        {isCompleted && <div className="absolute top-4 right-4 w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-bounce" data-id="element-181">
                            <Check className="w-5 h-5 text-white" data-id="element-182" />
                          </div>}
                        <div className="relative" data-id="element-183">
                          <div className={`w-14 h-14 bg-gradient-to-br ${getCategoryColor(quiz.category)} rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`} data-id="element-184">
                            <Award className="w-7 h-7 text-white" data-id="element-185" />
                          </div>
                          <div className="flex items-center space-x-2 mb-3" data-id="element-186">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${difficultyBadge.color}`} data-id="element-187">
                              {difficultyBadge.label}
                            </span>
                            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-188">
                              <Clock className="w-3 h-3 mr-1" data-id="element-189" />
                              <span data-id="element-190">{quiz.estimatedTime} min</span>
                            </div>
                          </div>
                          <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:gradient-text group-hover:from-green-600 group-hover:to-teal-600" data-id="element-191">
                            {quiz.title}
                          </h4>
                          <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2" data-id="element-192">
                            {quiz.description}
                          </p>
                          <div className="flex items-center justify-between" data-id="element-193">
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-194">
                              {quiz.questions.length} questions
                            </span>
                            <div className="flex items-center space-x-2 text-green-600 dark:text-green-400 font-semibold group-hover:translate-x-1 transition-transform" data-id="element-195">
                              <span data-id="element-196">
                                {isCompleted ? 'Play Again' : 'Start'}
                              </span>
                              <ArrowRight className="w-4 h-4" data-id="element-197" />
                            </div>
                          </div>
                        </div>
                      </button>;
            })}
                </div> : <div className="text-center py-16 glass-effect glass-effect-dark rounded-2xl border border-gray-200 dark:border-slate-700" data-id="element-198">
                  <div className="text-6xl mb-4 animate-bounce" data-id="element-199">🎯</div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg font-medium" data-id="element-200">
                    No quizzes available yet
                  </p>
                  <p className="text-gray-500 dark:text-gray-500 text-sm mt-2" data-id="element-201">
                    Check back soon for new challenges!
                  </p>
                </div>}
            </div>}
        </div>
      </div>
    </div>;
}
function getCategoryFromModule(moduleId: string): string {
  const mapping: Record<string, string> = {
    'emotional-intelligence': 'emotions',
    'body-positivity': 'health',
    'peer-relationships': 'relationships',
    communication: 'relationships',
    'online-safety': 'safety',
    consent: 'consent'
  };
  return mapping[moduleId] || 'emotions';
}